import { IFlowStep } from '../../../../utils/apollo/graphqlSchema';
import { PathNode } from './pathNode';
import { PathLink } from './pathLink';
import { assertNotNil } from '../../../../utils/types';
import { IPathNodeSetting, OthersConfigAction } from '../../interfaces';

type IPathNodeScore = Record<string, number>;

const OTHERS_NODE_NAME = 'Others';

type INodeFilter = (nodesInSequence: PathNode[], seq: number) => PathNode[];

/**
 * Builds a directed and weighted graph from Path FlowSteps. currentEvent and nextEvent from the step become nodes
 * (or vertex, represented as PathNode class) in the graph and the step itself becomes link (edge, represented as
 * PathLink) which weight is the count property of the step.
 *
 * It allows querying the graph (such as retrieving nodes up until specific sequence number) or manipulation -
 * such as merging nodes (for grouping high-cardinality steps)
 *
 */
export class PathGraph {
  /**
   * Creates an aggregated score of each node across all passed graphs. The score is a numeric value that allows
   * sorting the nodes. Nodes are sorted per sequence. The N top-most nodes (N=maxNodesPerSequence) remain unchanged,
   * while the rest are merged into a new node - "Others". All links (both incoming or outgoing) are redirected to and
   * from the new node while the merged nodes become "disconnected" (they still exist in the graph but there are no
   * connections to and from them).
   *
   * Important: The pathGraphs objects are mutated as part of the aggregation
   *
   * @param pathGraphs - a list of graphs to use to calculate score (across all of them)
   * @param maxNumberNodesPerSequence - max number of nodes per sequence before start merging into Others
   * @param pathNodeSettings - custom configuration of each node
   *
   * @return a map of node score: the key is the PathNode key, and the value is the score
   */
  public static mergeNodesByScore(
    pathGraphs: PathGraph[],
    maxNumberNodesPerSequence: number,
    pathNodeSettings: IPathNodeSetting[],
  ): IPathNodeScore {
    const settingsMap = this.settingsToOthersConfigMap(pathNodeSettings);

    const nodeScore: IPathNodeScore = {};
    // calculate the score across all graph first
    pathGraphs.forEach((graph) => graph.aggregateScore(nodeScore));
    // then perform merge based on the global score
    const mergeByScoreFilter: INodeFilter = (nodesInSequence) => {
      const sortByScore = (a: PathNode, b: PathNode) => {
        if (!a.isMerged() && !b.isMerged()) {
          return (nodeScore[b.key] ?? 0) - (nodeScore[a.key] ?? 0);
        } else if (a.isMerged() && !b.isMerged()) {
          return 1;
        } else if (!a.isMerged() && b.isMerged()) {
          return -1;
        } else {
          return 0;
        }
      };

      nodesInSequence.sort(sortByScore);

      nodesInSequence.forEach((node) => {
        node.othersConfigAction = settingsMap.get(node.name)?.get(node.nodeSeq);
      });

      const splitNodes = nodesInSequence.slice(0, maxNumberNodesPerSequence);
      const mergeNodes = nodesInSequence.slice(maxNumberNodesPerSequence);

      splitNodes.forEach((node) => {
        if (node.othersConfigAction === OthersConfigAction.COMBINE) {
          mergeNodes.push(node);
        }
      });

      const newMergeNodes = mergeNodes.filter((node) => {
        return node.othersConfigAction !== OthersConfigAction.SEPARATE;
      });

      newMergeNodes.sort(sortByScore);

      return newMergeNodes;
    };
    pathGraphs.forEach((graph) => graph.mergeNodes(mergeByScoreFilter));
    return nodeScore;
  }

  /**
   * Keeps the max sequence of path steps (the number of steps between start and end step)
   */
  public maxNodeSeq = 0;

  // lookup map to store all nodes in the graph by PathNode.key
  private nodesByKey: Record<string, PathNode> = {};

  constructor(steps: IFlowStep[]) {
    steps.forEach((step) => {
      const sourceNode = this._createOrGetNode(step.currentEvent, step.seq - 1);
      const destNode = this._createOrGetNode(step.nextEvent, step.seq);
      PathLink.create(sourceNode, destNode, step.count);
    });
  }

  /**
   * Returns a subset of all connected nodes in the graph up until the desired sequence (depth) from the beginning
   * of the flow. Additionally, it returns aggregated total of incoming weights per sequence to display purposes
   *
   * @param displaySeq - the maximum sequence (inclusive)
   */
  public getNodesUpToDisplaySeq(displaySeq: number) {
    const nodesToDisplaySeq = this._getConnectedNodes().filter(
      (node) => node.nodeSeq <= displaySeq,
    );
    const incomingTotalPerSeq: number[] = [];
    for (const node of nodesToDisplaySeq) {
      const weight = node.sumOfIncomingWeights();
      incomingTotalPerSeq[node.nodeSeq] =
        (incomingTotalPerSeq[node.nodeSeq] ?? 0) + weight;
    }
    return { nodesToDisplaySeq, incomingTotalPerSeq };
  }

  // ---------------------------
  // public for testing purposes
  // ---------------------------

  public _getNode(eventName: string, nodeSeq: number) {
    const pathNode = this.nodesByKey[this.generateKey(eventName, nodeSeq)];
    assertNotNil(pathNode);
    return pathNode;
  }

  public _getAllNodes() {
    return Object.values(this.nodesByKey);
  }

  public _getConnectedNodes() {
    return this._getAllNodes().filter(
      (node) =>
        node.incomingAdjacencyList().length > 0 ||
        node.outgoingAdjacencyList().length > 0,
    );
  }

  public _createOrGetNode(eventName: string, nodeSeq: number) {
    const key = this.generateKey(eventName, nodeSeq);
    if (this.nodesByKey.hasOwnProperty(key)) {
      return this.nodesByKey[key];
    } else {
      const node = new PathNode(key, eventName, nodeSeq);
      this.maxNodeSeq = Math.max(this.maxNodeSeq, nodeSeq);
      this.nodesByKey[key] = node;
      return node;
    }
  }

  /**
   * Performs a merge of nodes determined by the result of nodeFilter function
   *
   * @param nodeFilter - a function which accepts a list of nodes per
   *  each sequence and returns the nodes which must be merged into "Others" node
   */
  public mergeNodes(nodeFilter: INodeFilter) {
    const nodesBySeq = this.getNodesBySeq();
    for (let seq = 0; seq < nodesBySeq.length; seq++) {
      const nodesInSequence = nodesBySeq[seq];
      const nodesToMerge = nodeFilter(nodesInSequence, seq);
      if (nodesToMerge.length > 0) {
        const othersNode = this._createOrGetNode(OTHERS_NODE_NAME, seq);
        nodesToMerge.forEach((node) => othersNode.merge(node));
      }
    }
  }

  // ---------------------------
  // Private
  // ---------------------------

  /**
   * Calculate score of each node. Current implementation uses the sum of incoming steps as score/
   * The score object is mutated in the process
   *
   * @param score - map where the scores are aggergated
   */
  private aggregateScore(score: IPathNodeScore) {
    this._getAllNodes().forEach((node) => {
      if (!score.hasOwnProperty(node.key)) {
        score[node.key] = 0;
      }
      const weights = node.sumOfIncomingWeights();
      score[node.key] += weights;
    });
  }

  private generateKey(eventName: string, nodeSeq: number) {
    return `${nodeSeq}:${eventName}`;
  }

  private getNodesBySeq() {
    const nodesBySeq: PathNode[][] = [];
    for (const node of this._getAllNodes()) {
      if (!nodesBySeq[node.nodeSeq]) {
        nodesBySeq[node.nodeSeq] = [];
      }
      nodesBySeq[node.nodeSeq].push(node);
    }
    return nodesBySeq;
  }

  private static settingsToOthersConfigMap(
    pathNodeSettings: IPathNodeSetting[],
  ) {
    const map = new Map<string, Map<number, OthersConfigAction>>();
    for (const ns of pathNodeSettings) {
      const seqMap = new Map<number, OthersConfigAction>();
      map.set(ns.name, seqMap);
      for (const config of ns.othersConfig) {
        seqMap.set(config.seq, config.action);
      }
    }
    return map;
  }
}
